import type { InjectionKey, MaybeRefOrGetter, Ref } from 'vue';
import type { AnyItem, CompositePrimaryKey, GlobalSingleRelationFieldId, RelationOperationToMany, RelationOperationToOne } from '#layers/data-model';
import type { ResolvedMonospaceSingleRelationField } from '#layers/data-model/types/schema/resolved-schema';
import type { BaseFormControlProps } from '~/composables/form-block/use-base-form-field-state';
import type { CreateItem } from '~/composables/form-block/use-relation-field-state';
import { computed, inject, provide, toValue } from 'vue';
import { noop } from '#layers/base';
import { useBaseFormField } from '~/composables/form-block/use-base-form-field-state';
import { useProjectSchemaStore } from '~/stores/project-schema';

export interface RelationFormFieldContext<Kind extends 'to-many' | 'to-one' | never = never, RelationFieldValue extends RelationOperationToMany[] | RelationOperationToOne = Kind extends 'to-many' ? RelationOperationToMany[] : Kind extends 'to-one' ? RelationOperationToOne : RelationOperationToMany[] | RelationOperationToOne> {
	field: Ref<ResolvedMonospaceSingleRelationField | null>;
	fieldValue: Ref<RelationFieldValue | null>;
	referencedItemKey: Ref<CompositePrimaryKey | null>;
	disabled: MaybeRefOrGetter<boolean>;

	emitCreate: (payload: AnyItem) => void;
	emitUndoCreate: (payload: CreateItem) => void;
	emitConnect: (key: CompositePrimaryKey) => void;
	emitUndoConnect: (key: CompositePrimaryKey) => void;
	emitDisconnect: (key: CompositePrimaryKey) => void;
	emitUndoDisconnect: (key: CompositePrimaryKey) => void;
	emitDelete: (key: CompositePrimaryKey) => void;
	emitUndoDelete: (key: CompositePrimaryKey) => void;
	emitUpdate: (originalKey: CompositePrimaryKey | null, payload: AnyItem) => void;
}

export interface RelationFormControlProps extends BaseFormControlProps {
	fieldId?: GlobalSingleRelationFieldId;
}

/**
 * @internal
 */
const relationFormFieldContextKey = Symbol('relationFormFieldContextKey') as InjectionKey<RelationFormFieldContext>;

export function provideRelationFormFieldContext(context: RelationFormFieldContext) {
	provide(relationFormFieldContextKey, context);
}

/**
 * @internal
 */
function injectRelationFormFieldContext() {
	return inject(relationFormFieldContextKey, {
		field: computed(() => null),
		fieldValue: computed(() => null),
		referencedItemKey: computed(() => null),
		disabled: false,
		emitCreate: noop,
		emitUndoCreate: noop,
		emitConnect: noop,
		emitUndoConnect: noop,
		emitDisconnect: noop,
		emitUndoDisconnect: noop,
		emitDelete: noop,
		emitUndoDelete: noop,
		emitUpdate: noop,
	});
}

export function useRelationFormField<Kind extends 'to-many' | 'to-one' | never>(props: RelationFormControlProps) {
	const base = useBaseFormField(props);

	const context = injectRelationFormFieldContext() as RelationFormFieldContext<Kind>;
	const { findSingleRelationFieldById } = useProjectSchemaStore();

	// Combine base disabled (from form field context) with relation disabled (from relation block props)
	const disabled = computed(() => {
		const baseDisabled = base.disabled.value;
		const relationDisabled = toValue(context?.disabled);
		return baseDisabled || relationDisabled || false;
	});

	const field = computed(() => {
		const fieldId = toValue(props.fieldId);

		if (fieldId) {
			return findSingleRelationFieldById(fieldId) ?? context?.field.value ?? null;
		}

		return context?.field.value ?? null;
	});

	return {
		...base,
		...context,
		disabled,
		field,
	};
}
